import type { ReactElement, ReactNode, TransitionEvent } from 'react';
import React, {
  Children,
  cloneElement,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';

import withPosition from '../../hocs/withPosition/withPosition';
import { isSafari } from '../../utils/devices/device';
import Divider from './components/Divider/Divider';
import Item from './components/Item/Item';
import { AnimationPosition, Size, Variant } from './types';
import {
  ChildrenWrapper,
  Container,
  ContentWrapper,
  FooterComponentWrapper,
  HeaderComponentWrapper,
  Wrapper,
} from './List.styled';

export interface ListProps {
  children: ReactNode;
  /**
   * Flag to display the list
   */
  show: boolean;
  /**
   * Padding on the contents of the list
   * These are the options: `List.Size.SMALL` or `List.Size.MEDIUM`
   */
  size?: Size;
  /**
   * Component shown at the top of the list
   */
  HeaderComponent?: ReactElement;
  /**
   * Component shown at the bottom of the list
   */
  FooterComponent?: ReactElement;
  /**
   * Defines the number of items that will be visible
   */
  visibleItems?: number;
  /**
   * The width of the list.
   * If you want to adopt the width of the parent component, set to 100%
   */
  width: string;
  /**
   * Internal prop given by withPosition HOC used by [Popper.js](https://popper.js.org/docs/v2/)
   */
  maxWidth?: string;
  /**
   * Internal prop given by withPosition HOC used by [Popper.js](https://popper.js.org/docs/v2/)
   */
  position?: string;
  /**
   * Enables or disables list scrolling
   */
  preventScroll?: boolean;
  /**
   * Property used for testing purposes
   */
  dataCy?: string;
  /**
   * Internal prop given by withPosition HOC. Used by [Popper.js](https://popper.js.org/docs/v2/)
   */
  update?: () => void;

  /**
   * Maximum height of the list. In this case, the list has internal scroll
   */
  maxHeight?: number;

  /**
   * Variant of the list.
   * These are the options: `List.Variant.LIGHT` or `List.Variant.DARK`
   */
  variant?: Variant;

  /**
   * The role of the list.
   * This is used for accessibility purposes.
   */
  role?: string;

  /**
   * id of the list, used internally for accessibility purposes
   */
  id?: string;
}

interface Positions {
  [key: string]: AnimationPosition;
}

const positions: Positions = {
  top: AnimationPosition.TOP,
  bottom: AnimationPosition.BOTTOM,
  left: AnimationPosition.LEFT,
  right: AnimationPosition.RIGHT,
};

const getMaxHeight = (
  wrapperMaxHeight: number | undefined,
  childrenMaxHeight: number | undefined,
) => {
  if (wrapperMaxHeight && childrenMaxHeight) {
    return {
      wrapperMaxHeight:
        wrapperMaxHeight < childrenMaxHeight ? wrapperMaxHeight : undefined,
      childrenMaxHeight:
        childrenMaxHeight < wrapperMaxHeight ? childrenMaxHeight : undefined,
    };
  }

  return {
    wrapperMaxHeight,
    childrenMaxHeight,
  };
};

const List = forwardRef<HTMLDivElement, ListProps>(
  (
    {
      HeaderComponent,
      FooterComponent,
      children,
      show,
      size = Size.SMALL,
      visibleItems,
      width,
      maxWidth,
      position,
      preventScroll = false,
      dataCy,
      update,
      maxHeight,
      variant = Variant.LIGHT,
      role,
      id,
    },
    ref,
  ) => {
    const defaultAnimationPosition = AnimationPosition.BOTTOM;
    const [showContent, setShowContent] = useState<boolean>(show);
    const [childrenHeight, setChildrenHeight] = useState<number | undefined>();
    const localRef = useRef<HTMLDivElement>(null);
    const [animation, setAnimation] = useState<AnimationPosition>(
      defaultAnimationPosition,
    );

    useEffect(() => {
      if (show) {
        setShowContent(show);
      }
    }, [show]);

    useEffect(() => {
      if (showContent) {
        update?.();
      }
    }, [showContent, update]);

    const handleTransitionEnd = (event: TransitionEvent<HTMLDivElement>) => {
      if (event.propertyName === 'opacity' && !show) {
        setShowContent(false);
      }
    };

    useEffect(() => {
      if (localRef.current && visibleItems) {
        let finalHeight = 0;
        const childrenLength = localRef.current.children.length;

        const itemsLength = childrenLength > visibleItems ? visibleItems : childrenLength;

        for (let step = 0; step < itemsLength; step += 1) {
          const childHeight = localRef.current.children[step]?.clientHeight;

          finalHeight = childHeight ? finalHeight + childHeight : finalHeight;
        }

        setChildrenHeight(finalHeight);
      }
    }, [visibleItems, showContent, childrenHeight, update, children]);

    useEffect(() => {
      if (position) {
        const positionName = position.split('-')[0] ?? defaultAnimationPosition;
        const newPosition = positions[positionName];
        if (newPosition) {
          setAnimation(newPosition);
        }
      }
    }, [position, defaultAnimationPosition]);

    const shouldShowList = visibleItems ? !!childrenHeight && show : show;
    const { wrapperMaxHeight, childrenMaxHeight } = getMaxHeight(
      maxHeight,
      childrenHeight,
    );

    return (
      <Wrapper $show={shouldShowList}>
        <ContentWrapper
          ref={ref}
          data-cy={dataCy}
          $show={shouldShowList}
          onTransitionEnd={handleTransitionEnd}
          $width={width}
          $maxWidth={maxWidth}
          $maxHeight={wrapperMaxHeight}
          $animationPosition={animation}
          $isDarkMode={variant === Variant.DARK}
        >
          {showContent && (
            <Container $size={size} role={role} id={id}>
              {!!HeaderComponent && (
                <HeaderComponentWrapper>{HeaderComponent}</HeaderComponentWrapper>
              )}
              <ChildrenWrapper
                $height={childrenMaxHeight}
                $preventScroll={preventScroll}
                $isDarkMode={variant === Variant.DARK}
                ref={localRef}
              >
                {Children.toArray(children).map(child => {
                  const clonedChild = cloneElement(child as ReactElement, {
                    tabIndex: isSafari ? undefined : 0,
                    variant,
                  });

                  return clonedChild;
                })}
              </ChildrenWrapper>
              {!!FooterComponent && (
                <FooterComponentWrapper>{FooterComponent}</FooterComponentWrapper>
              )}
            </Container>
          )}
        </ContentWrapper>
      </Wrapper>
    );
  },
);

export const ListWithPosition = withPosition(List);

export default Object.assign(ListWithPosition, {
  Item,
  Divider,
  Size,
  Variant,
  Placement: withPosition.Placement,
});
